<!--
  ~ Copyright (c) 2023 Irian Software Development SRL. All Rights Reserved.
  ~ This software is the proprietary information of Irian Software Development SRL.
  ~ Use is subject to license and non-disclosure terms
  -->

<form [formGroup]="form">
  <div class="row">
    <div class="col-2 col-lg-1 p">
      <label>{{'fwl.name' | translate}}</label>
    </div>
    <div class="col-4 col-lg-3 p">
      <bs-autocomplete
        (queryChange)="filterFirm($event,'name')"
        (selected)="onAutoCompleteSelect($event)"
        *ngIf="new"
        [readonly]="found"
        [items]="firms"
        [delay]="500"
        [maxlength]="100"
        formControlName="name"
        class="full-width"
        data-testid="name-autocomplete"
        [displayWith]="getFirmDisplayLabel"
        [compareWith]="compareFirmByName">
        <ng-template bsItem let-value>
          <span>{{'fwl.name' | translate}}: {{value.name}} {{'fwl.cif' | translate}}: {{value.cif}} </span>
        </ng-template>
      </bs-autocomplete>
      <input *ngIf="!new" formControlName="name" maxlength="100" pInputText type="text" class="full-width" data-testid="name-input"/>
    </div>
  </div>

  <div class="row">
    <div class="col-2 col-lg-1 p">
      <label>{{'fwl.cif' | translate}}</label>
    </div>
    <div class="col-4 col-lg-3 p">
      <bs-autocomplete
        (queryChange)="filterFirm($event,'cif')"
        (selected)="onAutoCompleteSelect($event)"
        *ngIf="new"
        [readonly]="found"
        [items]="firms"
        [delay]="500"
        [maxlength]="14"
        class="full-width"
        [displayWith]="getFirmDisplayLabel"
        [compareWith]="compareFirmByName"
        data-testid="cif-autocomplete"
        formControlName="cif">
        <ng-template bsItem let-value>
          <span>{{'fwl.name' | translate}}: {{value.name}} {{'fwl.cif' | translate}}: {{value.cif}} </span>
        </ng-template>
      </bs-autocomplete>
      <input *ngIf="!new" formControlName="cif" maxlength="14" pInputText required type="text" class="full-width" data-testid="cif-input"/>
    </div>
  </div>
  <div class="row">
    <div class="col-2 col-lg-1 p">
      <label>{{'fwl.address' | translate}}</label>
    </div>
    <div class="col-4 col-lg-3 p">
      <input [readonly]="found" formControlName="address" maxlength="200" pInputText type="text" class="full-width" data-testid="address-input"/>
    </div>
  </div>
  <div class="row">
    <div class="col-2 col-lg-1 p">
      <label>{{'fwl.county' | translate}}</label>
    </div>
    <div class="col-4 col-lg-3 p">
      <input [readonly]="found" formControlName="county" maxlength="20" pInputText type="text" class="full-width" data-testid="county-input"/>
    </div>
  </div>
  <div class="row">
    <div class="col-2 col-lg-1 p">
      <label>{{'fwl.nrComReg' | translate}}</label>
    </div>
    <div class="col-4 col-lg-3 p">
      <input [readonly]="found" formControlName="nrComReg" maxlength="20" pInputText type="text" class="full-width" data-testid="nr-com-reg-input"/>
    </div>
  </div>
  <div class="row">
    <div class="col-2 col-lg-1 p">
      <label>{{'fwl.phone' | translate}}</label>
    </div>
    <div class="col-4 col-lg-3 p">
      <input [readonly]="found" formControlName="phone" pInputText type="text" class="full-width" data-testid="phone-input"/>
    </div>
  </div>
  <div class="row">
    <div class="col-2 col-lg-1 p">
      <label>{{'fwl.fax' | translate}}</label>
    </div>
    <div class="col-4 col-lg-3 p">
      <input [readonly]="found" formControlName="fax" pInputText type="text" class="full-width" data-testid="fax-input"/>
    </div>
  </div>
  <div class="row">
    <div class="col-2 col-lg-1 p">
      <label>{{'fwl.capital' | translate}}</label>
    </div>
    <div class="col-4 col-lg-3 p">
      <input [readonly]="found" formControlName="capital" pInputText type="number" class="full-width" data-testid="capital-input"/>
    </div>
  </div>

  <h3>{{'fwl.bankAccounts' | translate}}</h3>
  <div class="mb">
    <p-table *ngIf="!found" [responsive]="true" [value]="bankAccountsControl.value" data-testid="table">

      <ng-template pTemplate="header">
        <tr>
          <th>{{'fwl.bank' | translate}}</th>
          <th>{{'fwl.account' | translate}}</th>
          <th>
            <button (click)="newAccount()" icon="ui-icon-add" pButton type="button" data-testid="new-account-btn"></button>
          </th>
        </tr>
      </ng-template>

      <ng-template pTemplate="body" let-tableBankAccount let-editing="editing" let-ri="rowIndex">
        <tr [pEditableRow]="tableBankAccount">

          <td pEditableColumn>
            <p-cellEditor>
              <ng-template pTemplate="input">
                <input pInputText type="text" [(ngModel)]="tableBankAccount.bank" [ngModelOptions]="{standalone: true}"
                       required *ngIf="tableBankAccount===editableBankAccount" data-testid="bank-input">
                <div *ngIf="!(tableBankAccount===editableBankAccount)">{{tableBankAccount.bank}}</div>
              </ng-template>
              <ng-template pTemplate="output">
                {{tableBankAccount.bank}}
              </ng-template>
            </p-cellEditor>
          </td>

          <td pEditableColumn>
            <p-cellEditor>
              <ng-template pTemplate="input">
                <input pInputText type="text" [(ngModel)]="tableBankAccount.account"
                       [ngModelOptions]="{standalone: true}" required *ngIf="tableBankAccount===editableBankAccount" data-testid="account-input">
                <div *ngIf="!(tableBankAccount===editableBankAccount)">{{tableBankAccount.account}}</div>
              </ng-template>
              <ng-template pTemplate="output">
                {{tableBankAccount.account}}
              </ng-template>
            </p-cellEditor>
          </td>

          <td>
            <button (click)="editBankAccount(tableBankAccount)"
                    [disabled]="!(editableBankAccount===null)"
                    icon="ui-icon-edit" pButton pRipple
                    type="button" data-testid="edit-bank-account-btn"></button>
            <button (click)="saveBankAccount()" [disabled]="!(tableBankAccount===editableBankAccount)"
                    icon="ui-icon-check"
                    pButton pRipple
                    type="button" data-testid="save-bank-account-btn"></button>
            <button (click)="deleteBankAccount(tableBankAccount)" icon="ui-icon-delete" pButton pRipple
                    type="button" data-testid="delete-bank-account-btn"></button>
          </td>
        </tr>
      </ng-template>
    </p-table>
  </div>
</form>
<p-messages [(value)]="msgs"></p-messages>
